import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {goTo, toPriceStr} from '@/utils';
import {defaultCurrencySymbol} from '@/config';
import {
  RechargeCommissionGameRuleV2Item,
  RechargeCommissionRuleV2Group,
  UserListItem,
} from '../invitation.type';
import {AgentInfo} from '../agent.type';
import {useAgentDataStore} from './agent-data.store';
import AgentDataTable from './AgentDataTable';
import {useFissionV4AvailabilityStore} from '../fission-v4-availability.store';
import {referEarnWhiteAgentTreeImage} from '../white-theme-assets';
import InvitationRulesPopup from './invitation-rules-popup';
import {useRechargeCommissionRules} from './commission-rule.hooks';

const ReferEarnWheelCard = React.lazy(() => import('./refer-earn-wheel-card'));

const ReferEarnWheelFallback = () => (
  <section
    aria-label="Loading rewards"
    className="relative -mx-3 flex min-h-[42rem] items-center justify-center overflow-hidden bg-transparent px-3">
    <span className="h-8 w-8 animate-spin rounded-full border-[0.1875rem] border-[#F2D79E] border-t-[#F5BE36]" />
  </section>
);

const coinIcon = require('@/assets/imgs/invitation/invite-coin.webp');
const invitationSwipeImg = require('@/assets/imgs/invitation/swipe.webp');
const agentLevelAvatarYou = require('@/assets/imgs/invitation/referral-team/team-avatar-you.webp');
const agentLevelAvatarA = require('@/assets/imgs/invitation/referral-team/team-avatar-a.webp');
const agentLevelAvatarB = require('@/assets/imgs/invitation/referral-team/team-avatar-b.webp');
const agentLevelAvatarC = require('@/assets/imgs/invitation/referral-team/team-avatar-c.webp');

interface InvitationContentProps {
  login: boolean;
  hasPhone: boolean;
  userList?: UserListItem[];
  agentInfo?: AgentInfo;
  code: string;
  showAgentApply: boolean;
  onInvite: () => void;
  onRefreshCode: () => void;
  onAgentApply: () => void;
}

interface AgentStatusCardProps {
  login: boolean;
  agentInfo?: AgentInfo;
}

interface InviteRewardProps {
  userList?: UserListItem[];
  onInvite: () => void;
  onOpenRules: () => void;
}

interface SimpleTableProps {
  headers: React.ReactNode[];
  rows: string[][];
  columnsClassName: string;
  headerRowClassName?: string;
  headerCellBaseClassName?: string;
  headerCellClassName?: string;
  variant?: 'default' | 'commission' | 'level-up' | 'recharge-rebate';
}

interface FaqItemProps {
  title: string;
  children: React.ReactNode;
}

type AgentLevelTreeNode = {
  avatar: string;
  id: string;
  left: number;
  size: number;
  top: number;
};

type AgentLevelTreeLabel = {
  id: string;
  left: number;
  top: number;
  variant: 'a' | 'b' | 'c' | 'you';
  width: number;
};

type AgentLevelTreeConnector = {
  height: number;
  left: number;
  radius: number;
  top: number;
  width: number;
};

const agentLevelTreeNodes: AgentLevelTreeNode[] = [
  {
    id: 'you',
    avatar: agentLevelAvatarYou,
    left: 147.0782470703125,
    top: 12,
    size: 43.08137512207031,
  },
  ...[66.75146484375, 232.71630859375].map((left, index) => ({
    id: `a${index + 1}`,
    avatar: agentLevelAvatarA,
    left,
    top: index === 0 ? 72.1485481262207 : 72,
    size: 36,
  })),
  ...[29.91357421875, 112.39599609375, 195.05908203125, 277.54150390625].map(
    (left, index) => ({
      id: `b${index + 1}`,
      avatar: agentLevelAvatarB,
      left,
      top: 114,
      size: 28,
    }),
  ),
  ...[
    12.2705078125, 53.01171875, 95.74072265625, 136.48193359375,
    177.41552734375, 217.70166015625, 259.98828125, 300.7294921875,
  ].map((left, index) => ({
    id: `c${index + 1}`,
    avatar: agentLevelAvatarC,
    left,
    top: 165.69131469726562,
    size: 22,
  })),
];

const agentLevelTreeLabels: AgentLevelTreeLabel[] = [
  {
    id: 'you',
    left: 147.0782470703125,
    top: 56.88137435913086,
    width: 43.08137512207031,
    variant: 'you',
  },
  ...[
    {id: 'A1', left: 75.1260404586792},
    {id: 'A2', left: 241.090884208679},
  ].map(label => ({
    ...label,
    top: 109.65399169921875,
    width: 19.2508487701416,
    variant: 'a' as const,
  })),
  ...[
    {id: 'B1', left: 34.2881498336792},
    {id: 'B2', left: 116.770571708679},
    {id: 'B3', left: 199.433657646179},
    {id: 'B4', left: 281.916079521179},
  ].map(label => ({
    ...label,
    top: 151.64952087402344,
    width: 19.2508487701416,
    variant: 'b' as const,
  })),
  ...[
    {id: 'C1', left: 13.6450834274292},
    {id: 'C2', left: 54.3862943649292},
    {id: 'C3', left: 97.1152982711792},
    {id: 'C4', left: 137.856509208679},
    {id: 'C5', left: 178.790102958679},
    {id: 'C6', left: 219.076235771179},
    {id: 'C7', left: 261.362856864929},
    {id: 'C8', left: 302.104067802429},
  ].map(label => ({
    ...label,
    top: 191.0330352783203,
    width: 19.2508487701416,
    variant: 'c' as const,
  })),
];

const agentLevelTreeConnectors: AgentLevelTreeConnector[] = [
  {
    left: 84.25146484375,
    top: 33.99400329589844,
    width: 166.96484375,
    height: 56.1064453125,
    radius: 10.7998046875,
  },
  {
    left: 43.41357421875,
    top: 90.10078430175781,
    width: 83.482421875,
    height: 60.3291015625,
    radius: 10.7998046875,
  },
  {
    left: 208.55908203125,
    top: 90.10078430175781,
    width: 83.482421875,
    height: 60.6513671875,
    radius: 10.7998046875,
  },
  {
    left: 22.7705078125,
    top: 150.75189208984375,
    width: 41.7412109375,
    height: 14.9375,
    radius: 7,
  },
  {
    left: 106.24072265625,
    top: 150.75189208984375,
    width: 41.7412109375,
    height: 15.1982421875,
    radius: 7,
  },
  {
    left: 187.91552734375,
    top: 150.75189208984375,
    width: 41.2861328125,
    height: 15.5361328125,
    radius: 7,
  },
  {
    left: 270.48828125,
    top: 150.75189208984375,
    width: 41.7412109375,
    height: 14.9375,
    radius: 7,
  },
];

const toRem = (value: number) => `${value / 16}rem`;

const fallbackRewardSteps: UserListItem[] = [
  {
    inviteTaskUserId: 1,
    inviteNumber: 1,
    invitedNumber: 0,
    completedNumber: 0,
    depositAmount: 0,
    bonusAmount: 100,
    level: 1,
    status: 0,
  },
  {
    inviteTaskUserId: 2,
    inviteNumber: 3,
    invitedNumber: 0,
    completedNumber: 0,
    depositAmount: 0,
    bonusAmount: 300,
    level: 2,
    status: 0,
  },
  {
    inviteTaskUserId: 3,
    inviteNumber: 10,
    invitedNumber: 0,
    completedNumber: 0,
    depositAmount: 0,
    bonusAmount: 1000,
    level: 3,
    status: 0,
  },
  {
    inviteTaskUserId: 4,
    inviteNumber: 30,
    invitedNumber: 0,
    completedNumber: 0,
    depositAmount: 0,
    bonusAmount: 3000,
    level: 4,
    status: 0,
  },
  {
    inviteTaskUserId: 5,
    inviteNumber: 70,
    invitedNumber: 0,
    completedNumber: 0,
    depositAmount: 0,
    bonusAmount: 7500,
    level: 5,
    status: 0,
  },
  {
    inviteTaskUserId: 6,
    inviteNumber: 200,
    invitedNumber: 0,
    completedNumber: 0,
    depositAmount: 0,
    bonusAmount: 22000,
    level: 6,
    status: 0,
  },
];

const getInviteProgressCompletedNumber = (item?: UserListItem) =>
  item?.completedNumber ?? item?.invitedNumber ?? 0;

const inviteProgressMinItemWidthRem = 2.75;
const inviteProgressItemGapRem = 0.9375;
const inviteProgressMinContentWidthRem = 21.1875;
const inviteProgressScrollEdgeOffset = 2;

const getInviteProgressRewardText = (bonusAmount: number) =>
  `${defaultCurrencySymbol}${toPriceStr(bonusAmount || 0, {
    fixed: 0,
    showCurrency: false,
  })}`;

const getInviteProgressItemWidthRem = (rewardText: string) =>
  Math.max(inviteProgressMinItemWidthRem, rewardText.length * 0.4375 + 0.25);

const normalizeCommissionGameName = (gameName: string) =>
  gameName.trim().toLowerCase();

const formatCommissionGameLabel = (gameName: string) =>
  normalizeCommissionGameName(gameName) === 'sport' ? 'Sports' : gameName;

const formatCommissionRate = (value?: string) => {
  if (!value) {
    return '--';
  }

  return value
    .trim()
    .replace(/(\.\d*?[1-9])0+%$/, '$1%')
    .replace(/\.0+%$/, '%');
};

const getCommissionGameList = (ruleGroups: RechargeCommissionRuleV2Group[]) => {
  const gameByType = new Map<number, RechargeCommissionGameRuleV2Item>();

  ruleGroups.forEach(group => {
    group.gameList.forEach(game => {
      if (!gameByType.has(game.gameType)) {
        gameByType.set(game.gameType, game);
      }
    });
  });

  return Array.from(gameByType.values());
};

const getCommissionRows = (
  ruleGroups: RechargeCommissionRuleV2Group[],
  gameType?: number,
) => {
  if (gameType == null) {
    return [];
  }

  return [...ruleGroups]
    .sort((prev, next) => prev.agentLevel - next.agentLevel)
    .map(group => {
      const game = group.gameList.find(item => item.gameType === gameType);

      return [
        `Lv${group.agentLevel}`,
        formatCommissionRate(game?.level1Rate),
        formatCommissionRate(game?.level2Rate),
        formatCommissionRate(game?.level3Rate),
        formatCommissionRate(game?.level4Rate),
      ];
    });
};

const getCommissionHeaders = (t: ReturnType<typeof useTranslation>['t']) => [
  <span className="whitespace-pre-line">
    {t('invitation.referral.betCommission.table.agent')}
    <br />
    {t('invitation.referral.betCommission.table.level')}
  </span>,
  <span className="whitespace-nowrap">
    {t('invitation.referral.betCommission.table.teamA')}
  </span>,
  <span className="whitespace-nowrap">
    {t('invitation.referral.betCommission.table.teamB')}
  </span>,
  <span className="whitespace-nowrap">
    {t('invitation.referral.betCommission.table.teamC')}
  </span>,
  <span className="whitespace-nowrap">
    {t('invitation.referral.betCommission.table.teamD')}
  </span>,
];

const rechargeRebateFallbackRows = [
  ['200-600', '0.50%', '0.25%', '0.13%', '0.13%'],
  ['601-1,200', '1.00%', '0.50%', '0.25%', '0.25%'],
  ['1,201-2,000', '1.25%', '0.63%', '0.32%', '0.32%'],
  ['2,001-4,000', '1.30%', '0.65%', '0.33%', '0.33%'],
  ['4,001-10,000', '1.50%', '0.75%', '0.38%', '0.38%'],
  ['10,001-20,000', '2.00%', '1.00%', '0.50%', '0.50%'],
  ['20,001-999,999', '3.00%', '1.50%', '0.75%', '0.75%'],
];

const getRechargeRebateHeaders = (
  t: ReturnType<typeof useTranslation>['t'],
) => [
  t('invitation.referral.rechargeRebate.table.deposit'),
  t('invitation.referral.rechargeRebate.table.level1'),
  t('invitation.referral.rechargeRebate.table.level2'),
  t('invitation.referral.rechargeRebate.table.level3'),
  t('invitation.referral.rechargeRebate.table.level4'),
];

const getRechargeRebateRows = (ruleGroups: RechargeCommissionRuleV2Group[]) => {
  const rechargeRuleGroup = ruleGroups.find(group => group.list?.length);
  const rows =
    rechargeRuleGroup?.list?.map(item => [
      item.subDepositAmount,
      formatCommissionRate(item.level1Rate),
      formatCommissionRate(item.level2Rate),
      formatCommissionRate(item.level3Rate),
      formatCommissionRate(item.level4Rate),
    ]) || [];

  return rows.length ? rows : rechargeRebateFallbackRows;
};

const agentStatusCardClassName =
  'mt-[0.5625rem] min-h-[4.625rem] rounded-[0.625rem] bg-[#F4CFE3]';

export function AgentStatusCard({login, agentInfo}: AgentStatusCardProps) {
  const {t} = useTranslation();

  if (!login) {
    return (
      <section
        className={`${agentStatusCardClassName} flex items-center justify-center px-[1rem] py-[1.0625rem]`}>
        <button
          type="button"
          onClick={() => goTo('Login')}
          className="flex h-[2.5rem] w-[7.8125rem] cursor-pointer items-center justify-center rounded-[3.5rem] border-0 bg-[linear-gradient(180deg,#FFE206_0%,#FFD45E_100%)] text-[1rem] font-semibold leading-[1.802] text-[#1A1A21] [touch-action:manipulation] active:scale-[0.98]">
          {t('invitation.referral.loginNow')}
        </button>
      </section>
    );
  }

  return (
    <section className={`${agentStatusCardClassName} p-3`}>
      <div className="flex items-center justify-between gap-[0.75rem]">
        <div className="flex min-w-0 items-center gap-[0.5625rem]">
          {agentInfo?.userAvatar ? (
            <img
              src={agentInfo.userAvatar}
              alt=""
              className="h-[3.125rem] w-[3.125rem] shrink-0 rounded-full bg-[#F5E7BC] object-cover"
            />
          ) : (
            <span className="h-[3.125rem] w-[3.125rem] shrink-0 rounded-full bg-[#F5E7BC]" />
          )}
          <span className="w-[3.875rem] truncate text-[0.875rem] font-bold leading-normal text-black">
            Lv:{agentInfo?.agentLevel || 1}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-end gap-[0.5625rem] text-[0.875rem] leading-normal text-black">
          <div className="max-w-full text-right font-normal">
            {t('invitation.referral.totalUsers')}:{' '}
            {agentInfo?.totalUserCount || 0}
          </div>
          <div className="max-w-full text-right font-normal">
            {t('invitation.referral.totalCommission')}:{' '}
            {toPriceStr(Number(agentInfo?.totalCommissionAmount || 0), {
              thousands: true,
              fixed: 2,
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function InviteReward({userList, onInvite, onOpenRules}: InviteRewardProps) {
  const {t} = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false);
  const steps = useMemo(() => {
    const sourceList =
      userList && userList.length ? userList : fallbackRewardSteps;
    return sourceList.map((item, index) => {
      const rewardText = getInviteProgressRewardText(item.bonusAmount || 0);
      return {
        ...item,
        completedNumberForProgress: getInviteProgressCompletedNumber(item),
        level: item.level || index + 1,
        rewardText,
        widthRem: getInviteProgressItemWidthRem(rewardText),
      };
    });
  }, [userList]);
  const listLength = steps.length;
  const nextStep = useMemo(
    () =>
      steps.find(item => item.completedNumberForProgress < item.inviteNumber) ||
      steps[steps.length - 1],
    [steps],
  );
  const reachedIndex = useMemo(() => {
    return steps.reduce((latestIndex, item, index) => {
      return item.completedNumberForProgress >= item.inviteNumber
        ? index
        : latestIndex;
    }, -1);
  }, [steps]);
  const contentWidthRem = useMemo(() => {
    if (listLength <= 1) {
      return inviteProgressMinContentWidthRem;
    }
    return Math.max(
      inviteProgressMinContentWidthRem,
      steps.reduce((total, item) => total + item.widthRem, 0) +
        (listLength - 1) * inviteProgressItemGapRem,
    );
  }, [listLength, steps]);
  const progressInsetRem = useMemo(() => {
    const firstItem = steps[0];
    const lastItem = steps[steps.length - 1];
    return {
      left: (firstItem?.widthRem || inviteProgressMinItemWidthRem) / 2,
      right: (lastItem?.widthRem || inviteProgressMinItemWidthRem) / 2,
    };
  }, [steps]);
  const progressPercent = useMemo(() => {
    if (listLength <= 1 || reachedIndex < 0) {
      return 0;
    }
    return (reachedIndex / (listLength - 1)) * 100;
  }, [listLength, reachedIndex]);
  const remainingCount = Math.max(
    (nextStep?.inviteNumber || 0) - (nextStep?.completedNumberForProgress || 0),
    0,
  );
  const syncScrollEdge = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }
    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
    setIsAtScrollEnd(
      maxScrollLeft <= 0 ||
        scrollElement.scrollLeft >=
          maxScrollLeft - inviteProgressScrollEdgeOffset,
    );
  }, []);
  const toggleScrollEdge = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }
    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
    const shouldScrollLeft =
      maxScrollLeft > 0 &&
      scrollElement.scrollLeft >=
        maxScrollLeft - inviteProgressScrollEdgeOffset;
    scrollElement.scrollTo({
      left: shouldScrollLeft ? 0 : maxScrollLeft,
      behavior: 'smooth',
    });
    setIsAtScrollEnd(!shouldScrollLeft);
  }, []);

  useEffect(() => {
    syncScrollEdge();
  }, [contentWidthRem, syncScrollEdge]);

  return (
    <section className="relative mt-2 overflow-hidden rounded-[0.5rem] border border-[#FF8A00] bg-white/80 bg-clip-padding px-[0.375rem] pb-[1.125rem] pt-[0.9375rem]">
      <style>
        {`
          @keyframes invitation-progress-invite-button-breath {
            0%, 100% {
              transform: translateX(-50%) translateY(0) scale(1);
              filter: brightness(1);
            }
            44% {
              transform: translateX(-50%) translateY(0.1875rem) scale(1.025);
              filter: brightness(1.06) drop-shadow(0 0.25rem 0.45rem rgba(255, 179, 64, 0.34));
            }
            64% {
              transform: translateX(-50%) translateY(0.1rem) scale(1.012);
              filter: brightness(1.03) drop-shadow(0 0.1875rem 0.35rem rgba(255, 179, 64, 0.24));
            }
          }

          .invitation-progress-invite-button {
            animation: invitation-progress-invite-button-breath 1.75s ease-in-out infinite;
            transform-origin: center;
          }

          .invitation-progress-invite-button:active {
            animation-play-state: paused;
            transform: translateX(-50%) translateY(0.1875rem) scale(0.98);
          }
        `}
      </style>
      <div className="relative mx-auto h-[13.3125rem] max-w-[21.3125rem]">
        <button
          type="button"
          onClick={onOpenRules}
          className="absolute right-0 top-0 z-10 flex cursor-pointer items-center gap-[0.25rem] border-0 bg-transparent p-0 text-[0.875rem] font-medium leading-normal text-[#6E3A02] [touch-action:manipulation] active:scale-95">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.1875rem"
            height="1.1875rem"
            viewBox="0 0 19 19"
            fill="none"
            aria-hidden="true">
            <path
              d="M13.2367 4.63257H5.4895C4.75891 4.63257 4.16199 5.22949 4.16199 5.96008V14.372C4.16199 15.1025 4.75891 15.6995 5.4895 15.6995H13.2367C13.9673 15.6995 14.5642 15.1025 14.5642 14.372V5.96008C14.5642 5.22949 13.9673 4.63257 13.2367 4.63257ZM6.3977 7.2583H9.99573C10.2576 7.2583 10.47 7.4707 10.47 7.73254C10.47 7.99438 10.2576 8.20679 9.99573 8.20679H6.3977C6.13586 8.20679 5.92346 7.99438 5.92346 7.73254C5.92346 7.4707 6.13586 7.2583 6.3977 7.2583ZM8.63342 13.0737H6.39954C6.1377 13.0737 5.92529 12.8613 5.92529 12.5995C5.92529 12.3376 6.1377 12.1252 6.39954 12.1252H8.63342C8.89526 12.1252 9.10767 12.3376 9.10767 12.5995C9.10767 12.8613 8.89526 13.0737 8.63342 13.0737ZM12.0428 10.6403H6.3977C6.13586 10.6403 5.92346 10.4279 5.92346 10.166C5.92346 9.90418 6.13586 9.69177 6.3977 9.69177H12.0428C12.3047 9.69177 12.5171 9.90418 12.5171 10.166C12.5171 10.4279 12.3047 10.6403 12.0428 10.6403ZM6.3977 3.99902H12.0428C12.3047 3.99902 12.5171 3.78662 12.5171 3.52478C12.5171 3.26294 12.3047 3.05054 12.0428 3.05054H6.3977C6.13586 3.05054 5.92346 3.26294 5.92346 3.52478C5.92346 3.78662 6.13586 3.99902 6.3977 3.99902Z"
              fill="#6E3A02"
            />
          </svg>
          <span>{t('invitation.home.rules')}</span>
        </button>

        <div className="flex w-full flex-col items-stretch gap-[0.3125rem] pr-[6.25rem]">
          <p className="m-0 text-[0.9375rem] font-medium leading-normal text-black">
            {t('invitation.home.inviteGet', {count: remainingCount})}
          </p>
          <div className="text-[1.625rem] font-[700] leading-none text-[#6E3A02]">
            {defaultCurrencySymbol}&nbsp;
            {toPriceStr(nextStep?.bonusAmount || 0, {
              thousands: true,
              fixed: 0,
              showCurrency: false,
            })}
          </div>
        </div>

        <button
          type="button"
          className="absolute right-[-0.0625rem] top-[3.625rem] z-[2] flex size-[1.125rem] cursor-pointer items-center justify-center border-0 bg-transparent p-0 [touch-action:manipulation] active:scale-95"
          onClick={toggleScrollEdge}>
          <img
            alt=""
            className={[
              'size-[1.125rem] object-contain transition-transform',
              isAtScrollEnd ? 'rotate-180' : '',
            ].join(' ')}
            src={invitationSwipeImg}
          />
        </button>

        <div
          ref={scrollRef}
          className="absolute left-[-0.375rem] right-[-0.375rem] top-[4.375rem] overflow-x-auto overflow-y-hidden px-[0.1875rem] pb-[0.125rem] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={syncScrollEdge}>
          <div
            className="flex flex-col"
            style={{width: `${contentWidthRem}rem`}}>
            <div className="flex h-[3.375rem] items-end gap-[0.9375rem]">
              {steps.map((item, index) => {
                const active = index === reachedIndex;
                return (
                  <div
                    key={`${item.inviteTaskUserId || item.level}-${index}`}
                    className="flex shrink-0 flex-col items-center text-center"
                    style={{width: `${item.widthRem}rem`}}>
                    <img
                      src={coinIcon}
                      alt=""
                      className={[
                        'object-contain',
                        active
                          ? 'h-[2.4375rem] w-[2.4375rem]'
                          : 'h-[1.875rem] w-[1.875rem]',
                      ].join(' ')}
                    />
                    <span className="mt-0 w-full whitespace-nowrap text-center text-xs font-[700] leading-normal tracking-[0.01em] text-[#6E3A02] [font-family:din,Inter,sans-serif]">
                      {item.rewardText}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="relative mt-[0.625rem] h-[1rem]">
              <div
                className="absolute top-[0.25rem] h-[0.5rem] overflow-hidden rounded-[0.9375rem] bg-[#404040]"
                style={{
                  left: `${progressInsetRem.left}rem`,
                  right: `${progressInsetRem.right}rem`,
                }}>
                <div
                  className="h-full rounded-[0.9375rem] bg-[#6E3A02]"
                  style={{width: `${progressPercent}%`}}
                />
              </div>
              <div className="relative flex items-start gap-[0.9375rem]">
                {steps.map((item, index) => {
                  const reached =
                    item.completedNumberForProgress >= item.inviteNumber;
                  return (
                    <div
                      key={`${item.inviteTaskUserId || item.level}-${index}`}
                      className="flex shrink-0 justify-center"
                      style={{width: `${item.widthRem}rem`}}>
                      <span className="flex h-[1rem] w-[1rem] items-center justify-center rounded-full bg-[#191A1A]">
                        <span
                          className={[
                            'h-[0.5rem] w-[0.5rem] rounded-full shadow-[0_0.125rem_0.125rem_0_rgba(0,0,0,0.25)]',
                            reached ? 'bg-[#F8AF37]' : 'bg-[#9A8970]',
                          ].join(' ')}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-[0.0625rem] flex items-start gap-[0.9375rem]">
              {steps.map((item, index) => (
                <span
                  key={`${item.inviteTaskUserId || item.level}-${index}`}
                  className="shrink-0 whitespace-nowrap text-center text-[0.75rem] font-normal leading-normal text-[#564715]"
                  style={{width: `${item.widthRem}rem`}}>
                  {item.completedNumberForProgress}/{item.inviteNumber}
                </span>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onInvite}
          className="invitation-progress-invite-button absolute bottom-0 left-1/2 flex h-[2.5rem] w-[7.8125rem] cursor-pointer items-center justify-center rounded-[0.75rem] border-0 bg-[linear-gradient(180deg,#FFC85C_0%,#FFAF3D_100%)] text-[1rem] font-semibold leading-[1.802] text-black shadow-[0_0.1875rem_0.35625rem_0_rgba(255,143,0,0.28)] [touch-action:manipulation]">
          {t('invitation.referral.inviteNow')}
        </button>
      </div>
    </section>
  );
}

const agentLevelLabelClassNames: Record<
  AgentLevelTreeLabel['variant'],
  string
> = {
  you: 'text-[0.7875rem] leading-[0.9375rem] text-[rgba(240,187,28,0.9)]',
  a: 'bg-[linear-gradient(180deg,#FFFAFD_27.778%,#AE588C_86.111%)] bg-clip-text text-[0.675rem] leading-[0.81044rem] text-transparent',
  b: 'bg-[linear-gradient(180deg,#E3F1FF_27.778%,#3C4B6E_86.111%)] bg-clip-text text-[0.675rem] leading-[0.81044rem] text-transparent',
  c: 'text-[0.675rem] leading-[0.81044rem] text-white/90',
};

function AgentLevelTree() {
  const {t} = useTranslation();

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-[0.8125rem] top-[0.1875rem] h-[13.5rem] w-[20.625rem]">
      {agentLevelTreeConnectors.map(connector => (
        <span
          key={`${connector.left}-${connector.top}`}
          className="absolute z-0 box-border border-x border-t border-[#77808D]"
          style={{
            left: toRem(connector.left),
            top: toRem(connector.top),
            width: toRem(connector.width),
            height: toRem(connector.height),
            borderTopLeftRadius: toRem(connector.radius),
            borderTopRightRadius: toRem(connector.radius),
          }}
        />
      ))}
      {agentLevelTreeNodes.map(node => (
        <img
          key={node.id}
          src={node.avatar}
          alt=""
          className="absolute z-[1] block rounded-full object-cover"
          style={{
            left: toRem(node.left),
            top: toRem(node.top),
            width: toRem(node.size),
            height: toRem(node.size),
          }}
        />
      ))}
      {agentLevelTreeLabels.map(label => (
        <span
          key={label.id}
          className={[
            'absolute z-[2] block text-center font-inter font-medium tracking-[0.0084375rem]',
            agentLevelLabelClassNames[label.variant],
          ].join(' ')}
          style={{
            left: toRem(label.left),
            top: toRem(label.top),
            width: toRem(label.width),
          }}>
          {label.variant === 'you'
            ? t('invitation.referral.team.levels.you')
            : label.id}
        </span>
      ))}
    </div>
  );
}

function ReferralTeamIntro() {
  const {t} = useTranslation();

  return (
    <section className="mt-5">
      <div>
        <h2
          id="agent-level-description-title"
          className="m-0 text-left font-inter text-[0.9375rem] font-semibold leading-[1.125rem] text-[#2B2419]">
          {t('invitation.referral.team.title')}
        </h2>
        <article
          aria-labelledby="agent-level-description-title"
          className="relative -mx-[0.25rem] mt-[0.875rem] h-[22.75rem] w-[calc(100%+0.5rem)] overflow-hidden rounded-[0.5rem] border border-[#FF8A00] bg-white/80">
          <div className="hidden">
            <AgentLevelTree />
          </div>
          <img
            src={referEarnWhiteAgentTreeImage}
            alt=""
            className="pointer-events-none absolute left-1/2 top-[1.875rem] h-[12rem] w-[20rem] -translate-x-1/2 object-contain"
            draggable={false}
          />
          <div className="absolute left-[1.5625rem] top-[14.6875rem] flex h-[3.25rem] w-[9.5rem] flex-col justify-center text-center font-inter text-[0.675rem] font-medium leading-[0.75rem] tracking-[0.0084375rem] text-[#352A1A]">
            {(
              ['referralA', 'referralB', 'referralC', 'referralD'] as const
            ).map(level => (
              <p key={level} className="m-0 whitespace-nowrap">
                {t(`invitation.referral.team.levels.${level}`)}
              </p>
            ))}
          </div>
          <p className="absolute left-[1.625rem] top-[18.9375rem] m-0 w-[19.625rem] text-left font-inter text-[0.625rem] font-medium leading-[0.75rem] text-[#929AAA]">
            {t('invitation.referral.team.levels.description')}
          </p>
        </article>
      </div>
    </section>
  );
}

function SimpleTable({
  headers,
  rows,
  columnsClassName,
  variant = 'default',
  headerRowClassName,
  headerCellBaseClassName,
  headerCellClassName = 'whitespace-pre-line px-[0.25rem]',
}: SimpleTableProps) {
  if (variant === 'recharge-rebate') {
    return (
      <div className="mx-auto w-full overflow-hidden rounded-[0.625rem] border border-[#FF9207] bg-white font-inter">
        <div className="grid h-[2.1875rem] grid-cols-[1.28fr_repeat(4,1fr)] items-center bg-[#DFDFDF] text-center text-[0.75rem] font-normal leading-normal text-[#564715]">
          {headers.map((column, index) => (
            <div
              key={`${column}-${index}`}
              className="flex h-full items-center justify-center px-[0.25rem] text-center">
              {column}
            </div>
          ))}
        </div>
        <div>
          {rows.map((row, rowIndex) => (
            <div
              key={row.join('-')}
              className={[
                'grid h-[1.75rem] grid-cols-[1.28fr_repeat(4,1fr)] items-center text-center text-[0.625rem] font-normal leading-normal text-black',
                rowIndex % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
              ].join(' ')}>
              {row.map((cell, index) => (
                <div
                  key={`${cell}-${index}`}
                  className="flex h-full items-center justify-center px-[0.1875rem] text-center">
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'commission') {
    return (
      <div className="mx-auto w-full max-w-[20.9375rem] overflow-hidden font-inter">
        <div
          className={[
            'grid grid-cols-5 items-center text-[0.75rem] font-normal leading-[1.144rem] text-[#564715]',
            headerRowClassName || 'h-10',
            columnsClassName,
          ].join(' ')}>
          {headers.map((column, index) => (
            <div
              key={`${column}-${index}`}
              className={[
                'flex h-full items-center justify-center whitespace-pre-line bg-[#DFDFDF] px-[0.25rem] text-center',
                index === 0
                  ? 'justify-start rounded-l-[0.3125rem] pl-[0.40625rem] text-left'
                  : '',
                index === headers.length - 1 ? 'rounded-r-[0.3125rem]' : '',
              ].join(' ')}>
              {column}
            </div>
          ))}
        </div>
        <div className="mt-[0.1875rem]">
          {rows.map((row, rowIndex) => (
            <div
              key={row.join('-')}
              className={[
                'grid h-[2.5rem] grid-cols-5 items-center rounded-[0.3125rem] text-[0.75rem] font-normal leading-[1.144rem] text-black',
                columnsClassName,
                rowIndex % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
              ].join(' ')}>
              {row.map((cell, index) => (
                <div
                  key={`${cell}-${index}`}
                  className="flex h-full items-center justify-center px-[0.25rem] text-center">
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'level-up') {
    return (
      <div className="mx-auto w-full overflow-hidden font-inter">
        <div
          className={[
            'grid h-[2.1875rem] items-center grid-cols-[111fr_111fr_112fr] text-[0.75rem] font-normal leading-[1.144rem] text-[#564715]',
            columnsClassName,
          ].join(' ')}>
          {headers.map((column, index) => (
            <div
              key={`${column}-${index}`}
              className={[
                'flex h-full items-center justify-center whitespace-pre-line bg-[#FFEAA8] px-[0.25rem] text-center',
                index === 0 ? 'rounded-l-[0.3125rem]' : '',
                index === headers.length - 1 ? 'rounded-r-[0.3125rem]' : '',
              ].join(' ')}>
              {column}
            </div>
          ))}
        </div>
        <div>
          {rows.map((row, rowIndex) => (
            <div
              key={row.join('-')}
              className={[
                'grid h-[2.5rem] items-center grid-cols-[111fr_111fr_112fr] rounded-[0.3125rem] text-[0.75rem] font-normal leading-[1.144rem] text-black',
                columnsClassName,
                rowIndex % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
              ].join(' ')}>
              {row.map((cell, index) => (
                <div
                  key={`${cell}-${index}`}
                  className="flex h-full items-center justify-center px-[0.25rem] text-center">
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <AgentDataTable
      className="mt-0"
      loading={false}
      empty={false}
      emptyText=""
      gridClassName={columnsClassName}
      headerRowClassName={headerRowClassName}
      headerCellBaseClassName={headerCellBaseClassName}
      headerCellClassName={headerCellClassName}
      columns={headers}>
      {rows.map((row, rowIndex) => (
        <div
          key={row.join('-')}
          className={[
            'grid h-[2.5rem] items-center text-[0.75rem] font-normal leading-[1.144rem] text-[#352A1A]',
            columnsClassName,
            rowIndex % 2 === 1 ? 'bg-[#FFF5D7]/70' : '',
          ].join(' ')}>
          {row.map((cell, index) => (
            <div
              key={`${cell}-${index}`}
              className="flex h-full items-center justify-center px-[0.25rem] text-center">
              {cell}
            </div>
          ))}
        </div>
      ))}
    </AgentDataTable>
  );
}

function BetCommission({
  ruleGroups,
}: {
  ruleGroups: RechargeCommissionRuleV2Group[];
}) {
  const {t} = useTranslation();
  const [activeGameType, setActiveGameType] = useState<number>();
  const commissionTabListRef = useRef<HTMLDivElement | null>(null);
  const commissionTabButtonRefs = useRef<
    Record<number, HTMLButtonElement | null>
  >({});
  const commissionGameList = useMemo(
    () => getCommissionGameList(ruleGroups),
    [ruleGroups],
  );
  const activeGameInfo =
    (activeGameType != null
      ? commissionGameList.find(item => item.gameType === activeGameType)
      : undefined) || commissionGameList[0];
  const rows = getCommissionRows(ruleGroups, activeGameInfo?.gameType);
  const commissionHeaders = useMemo(() => getCommissionHeaders(t), [t]);
  const rechargeRebateHeaders = useMemo(() => getRechargeRebateHeaders(t), [t]);
  const rechargeRebateRows = useMemo(
    () => getRechargeRebateRows(ruleGroups),
    [ruleGroups],
  );
  const scrollCommissionTabIntoCenter = useCallback((gameType?: number) => {
    if (gameType == null) {
      return;
    }

    const tabList = commissionTabListRef.current;
    const tabButton = commissionTabButtonRefs.current[gameType];

    if (!tabList || !tabButton) {
      return;
    }

    const nextScrollLeft =
      tabButton.offsetLeft - (tabList.clientWidth - tabButton.offsetWidth) / 2;
    const maxScrollLeft = tabList.scrollWidth - tabList.clientWidth;

    tabList.scrollTo({
      left: Math.max(0, Math.min(nextScrollLeft, maxScrollLeft)),
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const hasActiveGame = commissionGameList.some(
      item => item.gameType === activeGameType,
    );
    const nextActiveGameType = commissionGameList[0]?.gameType;

    if (!hasActiveGame && nextActiveGameType != null) {
      setActiveGameType(nextActiveGameType);
    }
  }, [activeGameType, commissionGameList]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      scrollCommissionTabIntoCenter(activeGameInfo?.gameType);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeGameInfo?.gameType, scrollCommissionTabIntoCenter]);

  return (
    <section className="mt-5 font-inter">
      <h2 className="m-0 text-[0.9375rem] font-semibold leading-[1.125rem] text-[#2B2419]">
        {t('invitation.referral.betCommission.title')}
      </h2>
      <p className="m-0 mt-[0.875rem] text-[0.75rem] font-normal leading-[1.125rem] text-[#786A54]">
        {t('invitation.referral.betCommission.description')}
      </p>
      <div
        className={[
          'relative mt-[0.875rem] overflow-hidden rounded-[0.5rem] border border-[#FF8A00] bg-white/80 px-[0.4375rem] pb-[0.375rem]',
          commissionGameList.length === 1
            ? 'min-h-[fit-content] pt-0'
            : 'min-h-[21.625rem] pt-[0.8125rem]',
        ].join(' ')}>
        {commissionGameList.length > 1 ? (
          <div
            ref={commissionTabListRef}
            className="flex h-[2.1875rem] gap-[0.5rem] overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {commissionGameList.map(item => {
              const active = activeGameInfo?.gameType === item.gameType;
              return (
                <button
                  key={item.gameType}
                  ref={node => {
                    commissionTabButtonRefs.current[item.gameType] = node;
                  }}
                  type="button"
                  onClick={() => setActiveGameType(item.gameType)}
                  className={[
                    'flex h-[2.1875rem] shrink-0 grow-0 cursor-pointer items-center justify-center rounded-[0.5rem] text-center text-[0.75rem] font-normal leading-normal [touch-action:manipulation] active:scale-[0.99]',
                    commissionGameList.length >= 3
                      ? 'basis-[8.25rem]'
                      : 'basis-[calc((100%_-_0.5rem)/2)]',
                    active
                      ? 'border border-transparent bg-[linear-gradient(90deg,#FFD45E_2.214%,#FFB545_100%)] text-black'
                      : 'border border-solid border-[#DFDFDF] bg-white/0 text-[rgba(57,57,57,0.55)]',
                  ].join(' ')}>
                  <span className={active ? 'text-black' : 'text-inherit'}>
                    {formatCommissionGameLabel(item.gameTypeName)}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="mt-[0.875rem]">
          <SimpleTable
            variant="commission"
            columnsClassName=""
            headerRowClassName={
              commissionGameList.length === 1 ? 'h-[2.1875rem]' : 'h-10'
            }
            headers={commissionHeaders}
            rows={rows}
          />
        </div>
        <div className="mt-[0.875rem] flex items-start gap-[0.25rem]">
          <span className="mt-[0.4375rem] h-[0.25rem] w-[0.25rem] shrink-0 rounded-full bg-[#FF9207]" />
          <p className="m-0 whitespace-pre-line text-[0.625rem] font-normal leading-[1.125rem] text-[#929AAA]">
            {t('invitation.referral.betCommission.note')}
          </p>
        </div>
      </div>
      <div className="mt-5">
        <h2 className="m-0 text-[0.9375rem] font-semibold leading-[1.125rem] text-[#2B2419]">
          {t('invitation.referral.rechargeRebate.title')}
        </h2>
        <div className="mt-[0.875rem]">
          <SimpleTable
            variant="recharge-rebate"
            columnsClassName=""
            headers={rechargeRebateHeaders}
            rows={rechargeRebateRows}
          />
        </div>
      </div>
    </section>
  );
}

function AgentLevelUp() {
  const {t} = useTranslation();
  const ruleData = useAgentDataStore(state => state.ruleData);
  const loadRuleData = useAgentDataStore(state => state.loadRuleData);
  const rows = useMemo(
    () =>
      ruleData?.agentLevelList?.length
        ? ruleData.agentLevelList.map(item => [
            `Lv${item.level}`,
            `${item.teamMember || 0}`,
            toPriceStr(Number(item.teamRecharge || 0), {
              fixed: 0,
              thousands: true,
              showCurrency: false,
            }),
          ])
        : [],
    [ruleData],
  );

  useEffect(() => {
    loadRuleData();
  }, [loadRuleData]);

  return (
    <section className="mt-5">
      <div>
        <h2 className="m-0 text-[0.9375rem] font-semibold leading-[1.125rem] text-[#2B2419]">
          {t('invitation.referral.levelUp')}
        </h2>
        <div className="mt-[0.9375rem] min-h-[13.3125rem] overflow-hidden rounded-[0.625rem] border border-[#FF9207] bg-white py-[0.5rem]">
          <div className="px-[0.5rem]">
            <SimpleTable
              variant="level-up"
              columnsClassName="grid-cols-3"
              headers={[
                t('invitation.referral.levelUpTable.agentLevel'),
                t('invitation.referral.levelUpTable.teamMember'),
                t('invitation.referral.levelUpTable.teamDeposit'),
              ]}
              rows={rows}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqItem({title, children}: FaqItemProps) {
  return (
    <details
      open
      className="rounded-[0.625rem] border border-[#FF9207] bg-white px-[0.625rem] py-[0.6875rem] text-black">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-[0.75rem] text-[0.75rem] font-normal leading-[1.125rem] [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">{title}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="0.875rem"
          height="0.875rem"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
          className="shrink-0">
          <path
            d="M3.5 5L7 8.5L10.5 5"
            stroke="#9FA5AC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </summary>
      <div className="mt-[0.75rem] border-t border-[rgba(44,44,49,0.10)] pt-[0.75rem] text-[0.75rem] font-normal leading-[1.125rem] text-[#929AAA]">
        {children}
      </div>
    </details>
  );
}

function ReferralFaq() {
  const {t} = useTranslation();

  return (
    <section className="mt-5 pb-4">
      <div>
        <div className="flex items-end gap-[0.3125rem]">
          <span className="flex h-[1.1875rem] w-[1.1875rem] items-center justify-center rounded-[0.59375rem] border border-white bg-[rgba(127,129,133,0.81)] text-[0.875rem] font-semibold leading-none text-white shadow-[0_0_0.125rem_0_rgba(2,10,19,0.44)]">
            <span className="translate-x-[0.03125rem] translate-y-[0.0625rem]">
              ?
            </span>
          </span>
          <h2 className="m-0 text-[0.9375rem] font-semibold leading-[1.125rem] text-black">
            {t('invitation.referral.faq.title')}
          </h2>
        </div>
      </div>
      <div className="mt-[0.875rem] flex flex-col gap-[0.875rem]">
        <FaqItem title={t('invitation.referral.faq.how')}>
          <span className="block whitespace-pre-line">
            {t('invitation.referral.faq.howAnswer')}
          </span>
        </FaqItem>
        <FaqItem title={t('invitation.referral.faq.thanks')}>
          {t('invitation.referral.faq.thanksAnswer')}
        </FaqItem>
        <FaqItem title={t('invitation.referral.faq.requirement')}>
          <span className="block whitespace-pre-line">
            {t('invitation.referral.faq.requirementAnswer')}
          </span>
        </FaqItem>
      </div>
    </section>
  );
}

const InvitationContent = ({
  login,
  userList,
  agentInfo,
  onInvite,
}: InvitationContentProps) => {
  const {t} = useTranslation();
  const [rulesOpen, setRulesOpen] = useState(false);
  const wheelEnabled = useFissionV4AvailabilityStore(
    state => state.wheelEnabled,
  );
  const {rechargeRuleGroups, ruleThresholds} = useRechargeCommissionRules();

  return (
    <>
      {wheelEnabled && (
        <React.Suspense fallback={<ReferEarnWheelFallback />}>
          <ReferEarnWheelCard
            onInvite={onInvite}
            recordUser={{
              userAvatar: agentInfo?.userAvatar,
              userName: agentInfo?.userName,
            }}
          />
        </React.Suspense>
      )}
      <div className="-mx-3 flow-root bg-transparent px-3">
        {login && (
          <>
            <div className="mt-2 text-xs font-normal leading-[1.125rem] text-[#4E3D24]">
              {t('invitation.home.complete-task-prefix')}{' '}
              <span className="text-[0.9375rem] font-bold text-[#6E3A02]">
                33900
              </span>
            </div>
            <div className="mt-3">
              <InviteReward
                userList={userList}
                onInvite={onInvite}
                onOpenRules={() => setRulesOpen(true)}
              />
            </div>
          </>
        )}
        <ReferralTeamIntro />
        <BetCommission ruleGroups={rechargeRuleGroups} />
        <AgentLevelUp />
        <ReferralFaq />
      </div>
      <InvitationRulesPopup
        visible={rulesOpen}
        agentLevel={agentInfo?.agentLevel}
        initialSection="referral"
        rechargeRuleGroups={rechargeRuleGroups}
        thresholds={ruleThresholds}
        onClose={() => setRulesOpen(false)}
      />
    </>
  );
};

export default InvitationContent;
